// Google Analytics 4 helper. The global gtag loader lives in public/index.html
// with send_page_view:false so we control SPA page_views here (no duplicates).
export const GA_MEASUREMENT_ID = "G-LGM2C1FJQW";

// Normalize a route path for analytics. Drops query string and hash (so sensitive
// values like password-reset tokens are never sent) and masks the dynamic proposal
// id segment (so identifiers / PII are never sent to Google Analytics).
export function sanitizePath(pathname) {
  let path = (pathname || "/").split("?")[0].split("#")[0];
  path = path.replace(/^\/review\/[^/]+.*$/, "/review/:id");
  return path || "/";
}

// Send a single page_view for the given SPA path. No PII: page_title is static and
// page_location is rebuilt from origin + sanitized path (never the raw URL).
export function trackPageView(pathname) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  const path = sanitizePath(pathname);
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: "Proposal Forensics",
    send_to: GA_MEASUREMENT_ID,
  });
}

// Only allow generic, non-PII sign-up method values. Anything else (or an email/name)
// is dropped so we never send PII as the `method`.
const ALLOWED_SIGNUP_METHODS = ["email", "google", "github", "apple"];

// Fire the GA4 sign_up conversion. Call exactly once, right after a new company
// account is successfully created. Never pass email, name, company, id, or tokens.
export function trackSignUp(method) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return false;
  const params = { send_to: GA_MEASUREMENT_ID };
  if (ALLOWED_SIGNUP_METHODS.includes(method)) params.method = method;
  window.gtag("event", "sign_up", params);
  return true;
}

const PURCHASE_STORE_KEY = "pf_ga_purchases";

function firedPurchases() {
  try {
    return JSON.parse(window.localStorage.getItem(PURCHASE_STORE_KEY) || "[]");
  } catch {
    return [];
  }
}

export function hasFiredPurchase(transactionId) {
  return firedPurchases().includes(transactionId);
}

function markPurchaseFired(transactionId) {
  try {
    const arr = firedPurchases();
    if (!arr.includes(transactionId)) {
      arr.push(transactionId);
      window.localStorage.setItem(PURCHASE_STORE_KEY, JSON.stringify(arr.slice(-50)));
    }
  } catch {
    /* storage unavailable — best effort dedup only */
  }
}

// Fire the GA4 purchase conversion exactly once per confirmed transaction. `purchase`
// comes from the backend /payments/status response (already non-PII: session id,
// amount, currency, plan/package id + name). Deduped via localStorage so refresh or
// repeat navigation to the success page never re-fires.
export function trackPurchase(purchase) {
  if (!purchase || !purchase.transaction_id) return false;
  if (hasFiredPurchase(purchase.transaction_id)) return false;
  if (typeof window === "undefined" || typeof window.gtag !== "function") return false;
  window.gtag("event", "purchase", {
    transaction_id: purchase.transaction_id,
    value: purchase.value,
    currency: purchase.currency,
    items: [{ item_id: purchase.item_id, item_name: purchase.item_name }],
    send_to: GA_MEASUREMENT_ID,
  });
  markPurchaseFired(purchase.transaction_id);
  return true;
}

// Fire the GA4 standard login event after a successful authentication. Only a generic
// allow-listed method (e.g. "email") is included — never email address, name, or id.
export function trackLogin(method) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return false;
  const params = { send_to: GA_MEASUREMENT_ID };
  if (ALLOWED_SIGNUP_METHODS.includes(method)) params.method = method;
  window.gtag("event", "login", params);
  return true;
}

const DEMO_STORE_KEY = "pf_ga_demo";

function demoFired(key) {
  try {
    return JSON.parse(window.localStorage.getItem(DEMO_STORE_KEY) || "[]").includes(key);
  } catch {
    return false;
  }
}

function markDemoFired(key) {
  try {
    const arr = JSON.parse(window.localStorage.getItem(DEMO_STORE_KEY) || "[]");
    if (!arr.includes(key)) {
      arr.push(key);
      window.localStorage.setItem(DEMO_STORE_KEY, JSON.stringify(arr.slice(-100)));
    }
  } catch {
    /* storage unavailable — best effort dedup only */
  }
}

export function hasFiredDemo(key) {
  return demoFired(key);
}

// demo_start: fire when a free/demo analysis is actually initiated. proposalId is used
// only for local dedup — it is NOT sent to GA (no identifiers/PII in the payload).
export function trackDemoStart(proposalId) {
  if (!proposalId) return false;
  const key = `${proposalId}:start`;
  if (demoFired(key)) return false;
  if (typeof window === "undefined" || typeof window.gtag !== "function") return false;
  window.gtag("event", "demo_start", { send_to: GA_MEASUREMENT_ID });
  markDemoFired(key);
  return true;
}

// demo_complete: fire once after a demo analysis finishes successfully. Deduped per
// proposal so a page refresh/retry never re-fires. No identifiers/PII in the payload.
export function trackDemoComplete(proposalId) {
  if (!proposalId) return false;
  const key = `${proposalId}:complete`;
  if (demoFired(key)) return false;
  if (typeof window === "undefined" || typeof window.gtag !== "function") return false;
  window.gtag("event", "demo_complete", { send_to: GA_MEASUREMENT_ID });
  markDemoFired(key);
  return true;
}
